import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ShieldCheck, Search, Download, FileSpreadsheet, FileText, ChevronLeft, ChevronRight, Eye } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ROLE_LABEL } from "@/lib/constants";

const actionTone = (a) => {
  if (!a) return "muted";
  if (a.startsWith("CREATE")) return "success";
  if (a.startsWith("UPDATE") || a === "APPROVE") return "info";
  if (a.startsWith("DELETE") || a === "REJECT") return "destructive";
  if (a.startsWith("RESET") || a.startsWith("SEND") || a.startsWith("BROADCAST")) return "warning";
  return "muted";
};

export default function AuditLog() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const [filters, setFilters] = useState({
    actor: "", action: "", module: "", start_date: "", end_date: "",
  });
  const [distinct, setDistinct] = useState({ actions: [], modules: [] });

  const canView = ["SUPER_ADMIN","ADMIN_KLASIS","BP2K"].includes(user?.role);

  useEffect(() => {
    if (canView) {
      api.get("/audit-logs/actions").then((r) => setDistinct(r.data)).catch(() => {});
    }
  }, [canView]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", page); params.set("limit", limit);
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const { data } = await api.get(`/audit-logs?${params.toString()}`);
      setItems(data.items); setTotal(data.total);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (canView) load(); else setLoading(false); }, [page, canView]);

  const applyFilter = () => { setPage(1); load(); };

  const clearFilter = () => {
    setFilters({ actor: "", action: "", module: "", start_date: "", end_date: "" });
    setPage(1);
    setTimeout(load, 50);
  };

  const exportFile = async (format) => {
    try {
      const params = new URLSearchParams();
      params.set("format", format);
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const token = localStorage.getItem("simpel_token");
      const res = await fetch(`${API}/audit-logs/export?${params.toString()}`, {
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) { toast.error("Gagal export"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-log.${format === "xlsx" ? "xlsx" : format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Diekspor sebagai ${format.toUpperCase()}`);
    } catch (e) { toast.error("Gagal export"); }
  };

  const pageCount = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  if (!canView) {
    return (
      <AppShell>
        <div className="p-12 text-center border border-dashed border-border rounded-lg">
          <ShieldCheck className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <div className="text-sm text-muted-foreground">Hanya Super Admin, Admin Klasis, atau BP2K yang berhak melihat Audit Log.</div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Sistem • Pengawasan</div>
          <h1 className="font-heading font-bold text-3xl mt-1 flex items-center gap-3">
            <ShieldCheck className="h-7 w-7 text-secondary" /> Audit Log
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Riwayat semua mutasi penting di sistem: siapa mengubah apa, kapan, dan detail perubahannya. Ideal untuk pengawasan BP2K.
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="rounded-full" data-testid="btn-export">
              <Download className="h-4 w-4 mr-2" /> Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={()=>exportFile("csv")} data-testid="btn-export-csv">
              <FileText className="h-4 w-4 mr-2" /> CSV (Comma Separated)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={()=>exportFile("xlsx")} data-testid="btn-export-xlsx">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel (.xlsx)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={()=>exportFile("pdf")} data-testid="btn-export-pdf">
              <FileText className="h-4 w-4 mr-2" /> PDF (Cetak)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs">Aktor (nama)</Label>
              <Input value={filters.actor} onChange={(e)=>setFilters({...filters, actor:e.target.value})} placeholder="Cari nama…" data-testid="input-filter-actor" />
            </div>
            <div>
              <Label className="text-xs">Aksi</Label>
              <Select value={filters.action || "ALL"} onValueChange={(v)=>setFilters({...filters, action: v === "ALL" ? "" : v})}>
                <SelectTrigger data-testid="select-filter-action"><SelectValue placeholder="Semua aksi" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Semua Aksi</SelectItem>
                  {distinct.actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Modul</Label>
              <Select value={filters.module || "ALL"} onValueChange={(v)=>setFilters({...filters, module: v === "ALL" ? "" : v})}>
                <SelectTrigger data-testid="select-filter-module"><SelectValue placeholder="Semua modul" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Semua Modul</SelectItem>
                  {distinct.modules.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Dari Tanggal</Label>
              <Input type="date" value={filters.start_date} onChange={(e)=>setFilters({...filters, start_date:e.target.value})} data-testid="input-filter-start" />
            </div>
            <div>
              <Label className="text-xs">Sampai</Label>
              <Input type="date" value={filters.end_date} onChange={(e)=>setFilters({...filters, end_date:e.target.value})} data-testid="input-filter-end" />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <Button size="sm" onClick={applyFilter} className="rounded-full" data-testid="btn-apply-filter">
              <Search className="h-3.5 w-3.5 mr-1" /> Terapkan Filter
            </Button>
            <Button size="sm" variant="outline" onClick={clearFilter} className="rounded-full" data-testid="btn-clear-filter">
              Reset
            </Button>
            <div className="ml-auto text-xs text-muted-foreground">Total: <strong>{total}</strong> entri</div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? <div className="p-6 text-sm text-muted-foreground">Memuat…</div> : items.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">Tidak ada entri cocok dengan filter.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left p-3 font-semibold">Waktu (UTC)</th>
                    <th className="text-left p-3 font-semibold">Aktor</th>
                    <th className="text-left p-3 font-semibold">Aksi</th>
                    <th className="text-left p-3 font-semibold">Modul</th>
                    <th className="text-left p-3 font-semibold">Record</th>
                    <th className="text-right p-3 font-semibold">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} className="border-t border-border hover:bg-muted/20" data-testid={`row-audit-${it.id}`}>
                      <td className="p-3 whitespace-nowrap text-xs font-mono">{(it.timestamp || "").slice(0, 19).replace("T", " ")}</td>
                      <td className="p-3">
                        <div className="font-medium">{it.user_name || "—"}</div>
                        <div className="text-[0.7rem] text-muted-foreground">{ROLE_LABEL[it.role] || it.role || ""}</div>
                      </td>
                      <td className="p-3"><StatusBadge status={actionTone(it.action)} label={it.action} /></td>
                      <td className="p-3 text-xs text-muted-foreground">{it.module}</td>
                      <td className="p-3 text-[0.7rem] font-mono text-muted-foreground truncate max-w-[16ch]" title={it.record || ""}>{it.record ? it.record.slice(0, 8) + "…" : "—"}</td>
                      <td className="p-3 text-right">
                        {(it.before || it.after) && (
                          <Button size="sm" variant="ghost" onClick={()=>setDetail(it)} className="h-8 px-2" data-testid={`btn-detail-${it.id}`}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {items.length > 0 && (
            <div className="flex items-center justify-between p-4 border-t border-border text-xs">
              <div className="text-muted-foreground">Halaman {page} dari {pageCount}</div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={()=>setPage(page-1)} className="rounded-full h-8 w-8 p-0" data-testid="btn-prev-page">
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="outline" disabled={page >= pageCount} onClick={()=>setPage(page+1)} className="rounded-full h-8 w-8 p-0" data-testid="btn-next-page">
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={!!detail} onOpenChange={(v)=>!v && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Detail Entri Audit</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Waktu</Label><div className="font-mono">{detail.timestamp}</div></div>
                <div><Label className="text-xs">Aktor</Label><div>{detail.user_name} <span className="text-muted-foreground">({ROLE_LABEL[detail.role] || detail.role})</span></div></div>
                <div><Label className="text-xs">Aksi</Label><div><StatusBadge status={actionTone(detail.action)} label={detail.action} /></div></div>
                <div><Label className="text-xs">Modul</Label><div>{detail.module}</div></div>
                <div className="col-span-2"><Label className="text-xs">Record ID</Label><div className="font-mono text-[0.7rem]">{detail.record || "—"}</div></div>
              </div>
              {detail.before && (
                <div>
                  <Label className="text-xs">Sebelum</Label>
                  <pre className="bg-rose-50 border border-rose-200 rounded-md p-3 text-[0.7rem] overflow-x-auto max-h-40 whitespace-pre-wrap">{JSON.stringify(detail.before, null, 2)}</pre>
                </div>
              )}
              {detail.after && (
                <div>
                  <Label className="text-xs">Sesudah</Label>
                  <pre className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-[0.7rem] overflow-x-auto max-h-40 whitespace-pre-wrap">{JSON.stringify(detail.after, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
